import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset, Logout } from '@mui/icons-material';
import { changePassword } from '../api/agentApi';

interface Props {
  onSuccess: () => void;
  onLogout: () => void;
}

interface PasswordValidation {
  length: boolean;
  uppercase: boolean;
  digit: boolean;
  special: boolean;
}

const ForcePasswordChangeOverlay: React.FC<Props> = ({ onSuccess, onLogout }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real-time password validation
  const validatePassword = (password: string): PasswordValidation => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*]/.test(password),
    };
  };

  const validation = validatePassword(newPassword);
  const isPasswordValid = Object.values(validation).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = isPasswordValid && passwordsMatch && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Call changePassword without old password (admin-set password flow)
      await changePassword(newPassword);
      // Success - call onSuccess to clear the flag and refresh UI
      onSuccess();
    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err?.message || 'Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSubmit) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LockReset color="primary" />
          <Typography variant="h6" component="span">
            Password Change Required
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          Your password was set by an administrator. Please create a new password to continue using
          the application.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* New Password Field */}
        <Box mb={2}>
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSubmitting}
            autoFocus
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    disabled={isSubmitting}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Confirm Password Field */}
        <Box mb={3}>
          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSubmitting}
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={
              confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ''
            }
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={isSubmitting}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Password Requirements */}
        <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Password Requirements:
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 0 }}>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: validation.length ? 'success.main' : 'text.secondary',
                fontWeight: validation.length ? 600 : 400,
              }}
            >
              At least 8 characters
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: validation.uppercase ? 'success.main' : 'text.secondary',
                fontWeight: validation.uppercase ? 600 : 400,
              }}
            >
              At least one uppercase letter
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: validation.digit ? 'success.main' : 'text.secondary',
                fontWeight: validation.digit ? 600 : 400,
              }}
            >
              At least one digit
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: validation.special ? 'success.main' : 'text.secondary',
                fontWeight: validation.special ? 600 : 400,
              }}
            >
              At least one special character (!@#$%^&*)
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onLogout}
          startIcon={<Logout />}
          disabled={isSubmitting}
        >
          Logout
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <LockReset />}
        >
          {isSubmitting ? 'Changing Password...' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForcePasswordChangeOverlay;
